import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { resolveMyTcId } from "@/lib/api/tc-context";


export type SaleRow = {
  kind: "uniform" | "product";
  id: string;
  created_at: string;
  item_name: string;
  size: string | null;
  quantity: number;
  buyer: string;
  buyer_phone: string | null;
  buyer_cpf: string | null;
  shirt_name: string | null;
  shirt_number: string | null;
  amount: number;
  discount: number;
  net: number;
  payment_method: string;
  status: string;
  delivery_type: "imediata" | "futura" | null;
  expected_delivery_date: string | null;
  delivery_date: string | null;
  receiver_name: string | null;
  delivered_by_name: string | null;
  sold_by: string | null;
};

export const listSales = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);

    // Nome do usuário logado (dono do CT ou colaborador), usado como "vendedor"
    // para vendas registradas pela equipe (não há coluna de autor nos pedidos).
    let myName = "Equipe do CT";
    const { data: myProfile } = await context.supabase
      .from("profiles")
      .select("name")
      .eq("id", context.userId)
      .maybeSingle();
    if (myProfile?.name) {
      myName = myProfile.name;
    } else {
      const { data: myTc } = await context.supabase
        .from("training_centers")
        .select("manager_name")
        .eq("user_id", context.userId)
        .maybeSingle();
      if (myTc?.manager_name) myName = myTc.manager_name;
    }

    const [{ data: uOrders }, { data: pOrders }] = await Promise.all([
      context.supabase
        .from("uniform_orders")
        .select(
          "id,created_at,size,amount,discount,payment_method,status,delivery_type,expected_delivery_date,delivery_date,receiver_name,delivered_by_name,buyer_name,buyer_cpf,student_id,shirt_name,shirt_number,uniforms(name),students(name,parents(phone))",
        )
        .eq("training_center_id", tcId)
        .order("created_at", { ascending: false }),
      context.supabase
        .from("product_orders")
        .select(
          "id,created_at,quantity,amount,discount,payment_method,status,delivery_type,expected_delivery_date,delivery_date,receiver_name,delivered_by_name,buyer_name,buyer_cpf,student_id,products(name),students(name,parents(phone))",
        )
        .eq("training_center_id", tcId)
        .order("created_at", { ascending: false }),
    ]);

    const map = (rows: any[], kind: "uniform" | "product"): SaleRow[] =>
      (rows ?? []).map((r) => {
        const student = r.students;
        const parent = Array.isArray(student?.parents)
          ? student.parents[0]
          : student?.parents;
        const amount = Number(r.amount ?? 0);
        const discount = Number(r.discount ?? 0);
        return {
          kind,
          id: r.id,
          created_at: r.created_at,
          item_name:
            kind === "uniform"
              ? r.uniforms?.name ?? "Uniforme"
              : r.products?.name ?? "Mercadoria",
          size: kind === "uniform" ? r.size ?? null : null,
          quantity: kind === "product" ? Number(r.quantity ?? 1) : 1,
          buyer: student?.name ?? r.buyer_name ?? "Avulso",
          buyer_phone: parent?.phone ?? null,
          buyer_cpf: r.buyer_cpf ?? null,
          shirt_name: kind === "uniform" ? r.shirt_name ?? null : null,
          shirt_number: kind === "uniform" ? r.shirt_number ?? null : null,
          amount,
          discount,
          net: Math.max(0, amount - discount),
          payment_method: r.payment_method,
          status: r.status,
          delivery_type: r.delivery_type ?? null,
          expected_delivery_date: r.expected_delivery_date ?? null,
          delivery_date: r.delivery_date ?? null,
          receiver_name: r.receiver_name ?? null,
          delivered_by_name: r.delivered_by_name ?? null,
          // Pedidos feitos pelo cliente no app (portal do responsável) sempre gravam
          // nome/CPF do comprador mesmo quando vinculados a um aluno; vendas feitas
          // pela equipe zeram esses campos quando há aluno vinculado.
          sold_by: r.student_id && r.buyer_name
            ? "Compra feita pelo aplicativo (cliente)"
            : myName,
        };
      });

    const sales = [...map(uOrders ?? [], "uniform"), ...map(pOrders ?? [], "product")].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    return { sales };
  });

const deliverySchema = z.object({
  kind: z.enum(["uniform", "product"]),
  id: z.string().uuid(),
  receiver_name: z.string().trim().min(1).nullable().optional(),
  delivered_by_name: z.string().trim().min(1),
});

export const markSaleDelivered = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => deliverySchema.parse(d))
  .handler(async ({ context, data }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);
    const table = data.kind === "uniform" ? "uniform_orders" : "product_orders";
    const { error } = await context.supabase
      .from(table)
      .update({
        delivery_date: new Date().toISOString(),
        receiver_name: data.receiver_name ?? null,
        delivered_by_name: data.delivered_by_name,
      })
      .eq("id", data.id)
      .eq("training_center_id", tcId);
    if (error) throw error;
    return { ok: true };
  });

const undoSchema = z.object({
  kind: z.enum(["uniform", "product"]),
  id: z.string().uuid(),
});

export const undoSaleDelivered = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => undoSchema.parse(d))
  .handler(async ({ context, data }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);
    const table = data.kind === "uniform" ? "uniform_orders" : "product_orders";
    const { error } = await context.supabase
      .from(table)
      .update({ delivery_date: null, receiver_name: null, delivered_by_name: null })
      .eq("id", data.id)
      .eq("training_center_id", tcId);
    if (error) throw error;
    return { ok: true };
  });

/** Busca responsável/aluno pelo CPF do responsável (usado em vendas avulsas) */
export const lookupBuyerByCpf = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ cpf: z.string().min(1) }).parse(d))
  .handler(async ({ context, data }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);
    const digits = (data.cpf || "").replace(/\D/g, "");
    if (digits.length < 11) return { match: null };
    const { data: parent } = await context.supabase
      .from("parents")
      .select("id,name,phone,cpf,students(id,name,training_center_id)")
      .eq("training_center_id", tcId)
      .eq("cpf", digits)
      .maybeSingle();
    if (!parent) return { match: null };
    const students = (parent as any).students ?? [];
    const student = students.find((s: any) => s.training_center_id === tcId) ?? students[0] ?? null;
    return {
      match: {
        parent_id: (parent as any).id,
        parent_name: (parent as any).name,
        phone: (parent as any).phone,
        student_id: student?.id ?? null,
        student_name: student?.name ?? null,
      },
    };
  });

/** Exclui uma venda (uniforme ou mercadoria). Estorna estoque quando aplicável. */
export const deleteSale = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({
    kind: z.enum(["uniform", "product"]),
    id: z.string().uuid(),
  }).parse(d))
  .handler(async ({ context, data }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);
    const table = data.kind === "uniform" ? "uniform_orders" : "product_orders";
    // Remove movimentações e comprovantes vinculados
    await context.supabase.from("cash_movements")
      .delete().eq("source_table", table).eq("source_id", data.id);
    if (data.kind === "product") {
      // devolve estoque
      const { data: order } = await context.supabase
        .from("product_orders").select("product_id,quantity").eq("id", data.id).maybeSingle();
      if (order?.product_id) {
        const { data: p } = await context.supabase
          .from("products").select("stock").eq("id", order.product_id).maybeSingle();
        if (p && typeof p.stock === "number") {
          await context.supabase.from("products")
            .update({ stock: p.stock + (order.quantity ?? 1) }).eq("id", order.product_id);
        }
      }
    } else {
      const { data: order } = await context.supabase
        .from("uniform_orders").select("uniform_id").eq("id", data.id).maybeSingle();
      if (order?.uniform_id) {
        const { data: u } = await context.supabase
          .from("uniforms").select("stock").eq("id", order.uniform_id).maybeSingle();
        if (u && typeof u.stock === "number") {
          await context.supabase.from("uniforms")
            .update({ stock: u.stock + 1 }).eq("id", order.uniform_id);
        }
      }
    }
    const { error } = await context.supabase.from(table).delete()
      .eq("id", data.id).eq("training_center_id", tcId);
    if (error) throw error;
    return { ok: true };
  });

/** Atualiza valor e observação de uma venda pendente (para editar cobrança) */
export const updateSaleAmount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({
    kind: z.enum(["uniform", "product"]),
    id: z.string().uuid(),
    amount: z.coerce.number().min(0),
  }).parse(d))
  .handler(async ({ context, data }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);
    const table = data.kind === "uniform" ? "uniform_orders" : "product_orders";
    const { error } = await context.supabase.from(table)
      .update({ amount: data.amount })
      .eq("id", data.id).eq("training_center_id", tcId);
    if (error) throw error;
    return { ok: true };
  });

